// Mounts Cloudflare Turnstile inside every Worker-routed form, intercepts
// submit to fetch()-POST through the form-handling Worker, pushes a
// `lead_submit` event to dataLayer (GTM → GA4 / Meta / Google Ads / TikTok
// lead-event tags) on success, and renders a success / error message in
// the form's place so visitors never see the Worker's raw JSON response.
//
// Why fetch() instead of native form POST? The Worker returns JSON; a
// native POST navigates the visitor to that JSON. fetch() keeps the
// visitor on-page and gives us a hook to fire analytics + render UI.
//
// Forms opt in by carrying `data-tb-worker-form="<form_type>"`. The
// default success message can be overridden per form via
// `data-tb-success-message="…"` on the <form> element.
//
// Spec:
//   - docs/migration-tasks.md "reCAPTCHA v3 widget rendered on all forms"
//     (Turnstile drop-in 2026-05-23)
//   - docs/migration-tasks.md "Google Ads Tier 1 conversion tracking via
//     GTM" — the `lead_submit` dataLayer event is the trigger source
//   - docs/launch-open-items.md "Ad-pixel lead-event tags need trigger
//     rewire" — Meta / Google Ads / TikTok lead tags can also key on
//     this same dataLayer event

(function () {
  const tb = (window.taylorsBoats = window.taylorsBoats || {});
  const sitekey = tb.turnstileSiteKey;
  if (!sitekey) return;
  // No Worker URL configured (dev / pre-launch) — bail before mounting so
  // forms fall back to their default action (typically blank, which means
  // a same-page reload that surfaces no Klaviyo / inbox side-effects).
  if (!tb.formsWorkerUrl) return;

  const PENDING = '__tbPending';
  const WIDGET_ID = '__tbWidgetId';
  const DEFAULT_SUCCESS = "Thanks! We've received your submission and will be in touch shortly.";
  const DEFAULT_ERROR = 'Submission failed. Please refresh and try again.';

  let stylesInjected = false;
  function injectStylesOnce() {
    if (stylesInjected) return;
    stylesInjected = true;
    const style = document.createElement('style');
    style.textContent =
      '.tb-form-status{padding:1rem;text-align:center;border-radius:.5rem;font-size:1rem;line-height:1.5;color:#fff;margin:1rem 0;}' +
      '.tb-form-status--success{background-color:var(--primitives-status-success,#16a34a);}' +
      '.tb-form-status--error{background-color:var(--primitives-status-error,#dc2626);}';
    document.head.appendChild(style);
  }

  function attachToken(form, token) {
    let input = form.querySelector(':scope > input[name="cf-turnstile-response"]');
    if (!input) {
      input = document.createElement('input');
      input.type = 'hidden';
      input.name = 'cf-turnstile-response';
      form.appendChild(input);
    }
    input.value = token;
  }

  function mountAll() {
    if (typeof turnstile === 'undefined' || !turnstile.render) return;
    document.querySelectorAll('form[data-tb-worker-form]').forEach((form) => {
      if (form.dataset.tbTurnstileMounted) return;
      try {
        const div = document.createElement('div');
        div.className = 'tb-turnstile-mount';
        // Append as a direct child of the form. We avoid insertBefore
        // against the submit button — many of our forms nest the button
        // inside a wrapper div, so submitBtn is not a direct child and
        // insertBefore throws NotFoundError.
        form.appendChild(div);
        const widgetId = turnstile.render(div, {
          sitekey: sitekey,
          appearance: 'interaction-only',
          action: form.getAttribute('data-tb-worker-form') || 'submit',
        });
        form[WIDGET_ID] = widgetId;
        form.dataset.tbTurnstileMounted = '1';
      } catch (e) {
        console.error('[tb-forms] Turnstile mount failed:', e);
      }
    });
  }

  // Phone inputs carry no format constraint in markup (type="tel" alone does
  // not validate), so enforce a US-style 10-digit minimum — ignoring spaces,
  // dashes, parens — via custom validity so it surfaces in the same native
  // bubble as the other fields.
  const PHONE_MIN_DIGITS = 10;

  // The form carries `novalidate` and we bypass the native submit (preventDefault
  // + fetch), so the browser's constraint checks (required, type=email, the zip
  // `pattern`) never fire on their own. Run them explicitly before submitting.
  function isFormValid(form) {
    form.querySelectorAll('input[type="tel"]').forEach((input) => {
      const digits = input.value.replace(/\D/g, '');
      if (input.value.trim() && digits.length < PHONE_MIN_DIGITS) {
        input.setCustomValidity('Please enter a valid phone number (at least 10 digits).');
      } else {
        input.setCustomValidity('');
      }
    });
    // reportValidity() shows the native bubble and moves focus, but does not
    // set aria-invalid. Flag each control so screen readers can identify which
    // fields are in error (WCAG 3.3.1); cleared on the next valid pass.
    form.querySelectorAll('input, select, textarea').forEach((field) => {
      if (!field.willValidate) return;
      if (field.checkValidity()) {
        field.removeAttribute('aria-invalid');
      } else {
        field.setAttribute('aria-invalid', 'true');
      }
    });
    return form.reportValidity();
  }

  function boatContext() {
    const boat = (window.taylorsBoats && window.taylorsBoats.boat) || {};
    return {
      boat_product_id: boat.product_id || '',
      boat_stock_number: boat.stock_number || '',
      boat_year: boat.year || '',
      boat_make: boat.make || '',
      boat_model: boat.model || '',
    };
  }

  // One id per submit, shared between the server-side Meta CAPI Lead event
  // (sent by the Worker) and the browser Meta Lead tag (fired off this
  // dataLayer push). Meta dedups the two into a single conversion by event_id.
  function genEventId() {
    if (window.crypto && typeof window.crypto.randomUUID === 'function') {
      return window.crypto.randomUUID();
    }
    return 'tb-' + Date.now().toString(36) + '-' + Math.random().toString(36).slice(2, 10);
  }

  function readCookie(name) {
    const m = document.cookie.match('(?:^|; )' + name + '=([^;]*)');
    return m ? decodeURIComponent(m[1]) : '';
  }

  function pushLeadSubmit(form, eventId) {
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push(
      Object.assign(
        {
          event: 'lead_submit',
          form_type: form.getAttribute('data-tb-worker-form') || 'unknown',
          event_id: eventId || '',
        },
        boatContext(),
      ),
    );
  }

  function statusEl(kind, message) {
    injectStylesOnce();
    const div = document.createElement('div');
    div.className = 'tb-form-status tb-form-status--' + kind;
    div.setAttribute('role', kind === 'error' ? 'alert' : 'status');
    div.textContent = message;
    return div;
  }

  function renderSuccess(form) {
    const message = form.getAttribute('data-tb-success-message') || DEFAULT_SUCCESS;
    const div = statusEl('success', message);
    // Hide rather than remove so the form's wrapper layout stays intact
    // and any prior error banner above it gets cleared on success.
    const prior = form.previousElementSibling;
    if (prior && prior.classList && prior.classList.contains('tb-form-status--error')) {
      prior.remove();
    }
    form.style.display = 'none';
    form.parentNode.insertBefore(div, form);
  }

  function renderError(form, message) {
    const prior = form.previousElementSibling;
    if (prior && prior.classList && prior.classList.contains('tb-form-status--error')) {
      prior.remove();
    }
    const div = statusEl('error', message || DEFAULT_ERROR);
    form.parentNode.insertBefore(div, form);
    form[PENDING] = false;
    // Reset Turnstile so the visitor can retry without a page reload.
    if (typeof turnstile !== 'undefined' && turnstile.reset && form[WIDGET_ID] != null) {
      try {
        turnstile.reset(form[WIDGET_ID]);
      } catch (_) {}
    }
  }

  async function submitViaFetch(form) {
    const action = form.getAttribute('action') || tb.formsWorkerUrl;
    // Shared dedup id + Meta match signals ride in the POST body so the
    // Worker's CAPI Lead mirrors the browser pixel. _fbp/_fbc are Meta's own
    // click cookies; they're cross-origin to the Worker, so read and forward
    // them explicitly rather than relying on the request carrying cookies.
    const eventId = genEventId();
    const fd = new FormData(form);
    fd.append('tb_event_id', eventId);
    fd.append('tb_event_source_url', location.href);
    // Marketing consent gates the Meta side. The lead email + Klaviyo profile
    // are first-party (the visitor handed us their email for a quote) and run
    // regardless; only the ad-network mirror (Meta CAPI + the _fbp/_fbc click
    // ids) is consent-gated. When `window.tbConsent` is absent (banner/GTM not
    // wired yet) marketingAllowed() defaults to true, preserving prior behavior.
    const marketingOk =
      window.tbConsent && window.tbConsent.marketingAllowed
        ? window.tbConsent.marketingAllowed()
        : true;
    fd.append('tb_consent_marketing', marketingOk ? 'granted' : 'denied');
    if (marketingOk) {
      const fbp = readCookie('_fbp');
      if (fbp) fd.append('tb_fbp', fbp);
      const fbc = readCookie('_fbc');
      if (fbc) fd.append('tb_fbc', fbc);
    }
    let res, data;
    try {
      res = await fetch(action, { method: 'POST', body: fd });
    } catch (e) {
      renderError(form, 'Network error. Please check your connection and try again.');
      return;
    }
    try {
      data = await res.json();
    } catch (_) {
      data = {};
    }
    // The Worker returns `{status:"ok"}` for honeypot-tripped requests
    // with no `form_type` echoed back — we only fire the dataLayer event
    // when the response confirms a real form was processed.
    if (res.ok && data.status === 'ok') {
      if (data.form_type) pushLeadSubmit(form, eventId);
      renderSuccess(form);
    } else {
      renderError(form, (data && data.error) || DEFAULT_ERROR);
    }
  }

  function onSubmit(event) {
    const form = event.target;
    if (!(form instanceof HTMLFormElement)) return;
    if (!form.hasAttribute('data-tb-worker-form')) return;
    if (form[PENDING]) {
      event.preventDefault();
      return;
    }
    // Block submission on invalid input before anything else (Turnstile,
    // fetch). reportValidity() shows the native error bubbles.
    if (!isFormValid(form)) {
      event.preventDefault();
      return;
    }
    if (typeof turnstile === 'undefined' || !turnstile.execute) return;
    const widgetId = form[WIDGET_ID];
    if (widgetId == null) return;

    event.preventDefault();
    form[PENDING] = true;

    function withToken(token) {
      attachToken(form, token);
      submitViaFetch(form);
    }

    const existing = turnstile.getResponse(widgetId);
    if (existing) {
      withToken(existing);
      return;
    }

    turnstile.execute(widgetId, {
      callback: withToken,
      'error-callback': function () {
        renderError(form, 'Captcha verification failed. Please refresh and try again.');
      },
      'expired-callback': function () {
        form[PENDING] = false;
      },
    });
  }

  document.addEventListener('submit', onSubmit, true);

  // Turnstile's loader calls this when api.js finishes (we pass
  // ?onload=onloadTurnstileCallback on the script src).
  window.onloadTurnstileCallback = mountAll;

  if (document.readyState !== 'loading') {
    mountAll();
  } else {
    document.addEventListener('DOMContentLoaded', mountAll);
  }
})();
